import { professorOcupadoNoSlot, type OcupacaoProfessorSlot } from '../grade-conflito';
import type { HorarioTurnoTemplate, Turno } from '../../../types/models';

export type ProfResumoOcioso = { id: string; nome: string };

export type TurnoCor = {
  key: 'manha' | 'tarde' | 'noite' | 'outro';
  label: string;
  bg: string;
  fg: string;
  border: string;
  headBg: string;
  headFg: string;
};

export type SlotTurnoInfo = {
  numeroAula: number;
  ordem: number;
  horaInicio: string;
  horaFim: string;
};

export type TurnoOpcao = {
  turnoId: string;
  turnoNome: string;
  cor: TurnoCor;
  slots: SlotTurnoInfo[];
};

export type CelulaAula = {
  ordem: number;
  livre: boolean;
  horaInicio: string;
  horaFim: string;
};

export type LinhaDisponibilidade = {
  professor: ProfResumoOcioso;
  aulas: CelulaAula[];
  nLivres: number;
};

export type RelatorioDisponibilidade = {
  parados: ProfResumoOcioso[];
  totalAtivos: number;
  totalComCarga: number;
  dias: string[];
  turnos: TurnoOpcao[];
  /** Contagem de livres por ordem de aula (no turno/dia atual) */
  livresPorAula: number[];
  linhas: LinhaDisponibilidade[];
};

function hm(t?: string | null): string {
  if (!t) return '';
  return String(t).trim().slice(0, 5);
}

function aulasDoTemplate(template?: HorarioTurnoTemplate[] | null) {
  return (template ?? [])
    .filter((s) => (s.tipo || 'AULA') === 'AULA')
    .slice()
    .sort((a, b) => a.numeroAula - b.numeroAula || hm(a.horaInicio).localeCompare(hm(b.horaInicio)));
}

function toResumo(
  professores: Array<{ id: string; nome?: string | null; ativo?: boolean | null }>,
): ProfResumoOcioso[] {
  return professores
    .filter((p) => p.ativo !== false)
    .map((p) => ({ id: p.id, nome: (p.nome || 'Professor').trim() }))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

export function corDoTurno(nome: string): TurnoCor {
  const n = (nome || '').toLowerCase();
  if (n.includes('manhã') || n.includes('manha') || n.includes('matutino') || n.includes('morning')) {
    return {
      key: 'manha',
      label: 'Manhã',
      bg: '#dcfce7',
      fg: '#15803d',
      border: '#86efac',
      headBg: '#f0fdf4',
      headFg: '#166534',
    };
  }
  if (n.includes('tarde') || n.includes('vespertino') || n.includes('afternoon')) {
    return {
      key: 'tarde',
      label: 'Tarde',
      bg: '#ffedd5',
      fg: '#c2410c',
      border: '#fdba74',
      headBg: '#fff7ed',
      headFg: '#9a3412',
    };
  }
  if (n.includes('noite') || n.includes('noturno') || n.includes('night')) {
    return {
      key: 'noite',
      label: 'Noite',
      bg: '#dbeafe',
      fg: '#1d4ed8',
      border: '#93c5fd',
      headBg: '#eff6ff',
      headFg: '#1e40af',
    };
  }
  return {
    key: 'outro',
    label: nome || 'Turno',
    bg: '#e0e7ff',
    fg: '#4338ca',
    border: '#a5b4fc',
    headBg: '#eef2ff',
    headFg: '#3730a3',
  };
}

export function diasDisponiveisNosTurnos(turnos: Turno[]): string[] {
  const ordem = ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'];
  const set = new Set<string>();
  for (const t of turnos) {
    if (t.ativo === false) continue;
    for (const d of t.diasFuncionamento ?? []) set.add(d);
  }
  if (set.size === 0) return ordem.slice(0, 5);
  return ordem.filter((d) => set.has(d));
}

function montarTurnos(turnos: Turno[]): TurnoOpcao[] {
  return turnos
    .filter((t) => t.ativo !== false)
    .slice()
    .sort((a, b) =>
      String(a.horaAberturaPortoes || a.horaEntradaOficial || '').localeCompare(
        String(b.horaAberturaPortoes || b.horaEntradaOficial || ''),
      ),
    )
    .map((turno) => {
      const cor = corDoTurno(turno.nome);
      const aulas = aulasDoTemplate(turno.horariosTemplate);
      return {
        turnoId: turno.id,
        turnoNome: turno.nome,
        cor,
        slots: aulas.map((aula, idx) => ({
          numeroAula: aula.numeroAula,
          ordem: idx + 1,
          horaInicio: hm(aula.horaInicio),
          horaFim: hm(aula.horaFim),
        })),
      };
    });
}

/**
 * Visão prática: 1 turno + 1 dia → matriz professor × aulas (livre/ocupado).
 * Ordena quem tem mais folga primeiro.
 */
export function buildDisponibilidadeDiaTurno(
  turnos: Turno[],
  professores: Array<{ id: string; nome?: string | null; ativo?: boolean | null }>,
  ocupacao: OcupacaoProfessorSlot[],
  opts: { turnoId: string; diaSemana: string; soComFolga?: boolean },
): RelatorioDisponibilidade {
  const ativos = toResumo(professores);
  const comAlgumaAula = new Set(ocupacao.map((o) => o.professorId).filter(Boolean));
  const parados = ativos.filter((p) => !comAlgumaAula.has(p.id));
  const comCarga = ativos.filter((p) => comAlgumaAula.has(p.id));
  const dias = diasDisponiveisNosTurnos(turnos);
  const turnosOp = montarTurnos(turnos);
  const turno = turnosOp.find((t) => t.turnoId === opts.turnoId) ?? turnosOp[0];
  const slots = turno?.slots ?? [];
  const livresPorAula = slots.map(() => 0);

  let linhas: LinhaDisponibilidade[] = comCarga.map((professor) => {
    const aulas: CelulaAula[] = slots.map((slot, idx) => {
      const ocupado = professorOcupadoNoSlot(ocupacao, {
        professorId: professor.id,
        diaSemana: opts.diaSemana,
        numeroAula: slot.numeroAula,
        horaInicio: slot.horaInicio,
        horaFim: slot.horaFim,
        turnoOperacionalId: turno!.turnoId,
      });
      const livre = !ocupado;
      if (livre) livresPorAula[idx] += 1;
      return {
        ordem: slot.ordem,
        livre,
        horaInicio: slot.horaInicio,
        horaFim: slot.horaFim,
      };
    });
    return {
      professor,
      aulas,
      nLivres: aulas.filter((a) => a.livre).length,
    };
  });

  if (opts.soComFolga) {
    linhas = linhas.filter((l) => l.nLivres > 0);
  }

  linhas.sort(
    (a, b) =>
      b.nLivres - a.nLivres ||
      a.professor.nome.localeCompare(b.professor.nome, 'pt-BR'),
  );

  return {
    parados,
    totalAtivos: ativos.length,
    totalComCarga: comCarga.length,
    dias,
    turnos: turnosOp,
    livresPorAula,
    linhas,
  };
}

/** @deprecated use buildDisponibilidadeDiaTurno — mantido se algum import antigo restar */
export function buildOciososPorTurno(
  turnos: Turno[],
  professores: Array<{ id: string; nome?: string | null; ativo?: boolean | null }>,
  ocupacao: OcupacaoProfessorSlot[],
  dias?: string[],
) {
  const diasCols = dias?.length ? dias : diasDisponiveisNosTurnos(turnos);
  const turnosOp = montarTurnos(turnos);
  const first = turnosOp[0];
  return buildDisponibilidadeDiaTurno(turnos, professores, ocupacao, {
    turnoId: first?.turnoId ?? '',
    diaSemana: diasCols[0] ?? 'SEGUNDA',
  });
}
